import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent
} from "@/components/ui/chart";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

// User Growth Line Chart
interface UserGrowthChartProps {
  data: Array<{ date: string; users: number }>;
  isLoading?: boolean;
}

export const UserGrowthChart: React.FC<UserGrowthChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null; // Don't render the chart if there's no data
  }

  // Format date for display and improve tooltip
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    
    return dateStr.includes('T')
      ? `${date.toLocaleDateString()} ${date.getHours()}:00`
      : date.toLocaleDateString();
  };

  // Custom tooltip content for more readable dates
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-3 rounded-lg shadow-md">
          <p className="font-semibold">{formatDate(label)}</p>
          <p className="text-primary">{`Users: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="col-span-4">
      <CardHeader>
        <CardTitle>User Growth</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[250px] w-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                minTickGap={30}
              />
              <YAxis />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone"
                dataKey="users"
                name="Total Users" 
                stroke="#2563eb"
                activeDot={{ r: 8 }}
                isAnimationActive={true}
                animationDuration={1000}
                dot={{ strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Prompt Interactions Bar Chart
interface PromptInteractionsChartProps {
  data: Array<{ date: string; copies: number; saves: number; collections: number }>;
  isLoading?: boolean;
}

export const PromptInteractionsChart: React.FC<PromptInteractionsChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null; // Don't render the chart if there's no data
  }

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    
    return dateStr.includes('T')
      ? `${date.toLocaleDateString()} ${date.getHours()}:00`
      : date.toLocaleDateString();
  };

  // Custom tooltip content for more readable dates and values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-3 rounded-lg shadow-md">
          <p className="font-semibold">{formatDate(label)}</p>
          {payload.map((item: any, index: number) => (
            <p key={index} style={{ color: item.color }}>
              {`${item.name}: ${item.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="col-span-4">
      <CardHeader>
        <CardTitle>Prompt Interactions</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[250px] w-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                minTickGap={30}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="copies" name="Copies" fill="#2563eb" />
              <Bar dataKey="saves" name="Saves" fill="#10b981" />
              <Bar dataKey="collections" name="Collections" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// INDIVIDUAL CHARTS FOR EACH METRIC

// Prompt Copies Chart
interface PromptCopiesChartProps {
  data: Array<{ date: string; copies: number }>;
  isLoading?: boolean;
}

export const PromptCopiesChart: React.FC<PromptCopiesChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return dateStr.includes('T')
      ? `${date.toLocaleDateString()} ${date.getHours()}:00`
      : date.toLocaleDateString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-3 rounded-lg shadow-md">
          <p className="font-semibold">{formatDate(label)}</p>
          <p className="text-blue-500">{`Copies: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="col-span-4 lg:col-span-2">
      <CardHeader>
        <CardTitle>Prompt Copies</CardTitle>
      </CardHeader>
      <CardContent className="h-[250px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                minTickGap={30}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="copies" 
                name="Copies" 
                stroke="#2563eb"
                fill="#3b82f680" 
                activeDot={{ r: 8 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Prompt Saves Chart
interface PromptSavesChartProps {
  data: Array<{ date: string; saves: number }>;
  isLoading?: boolean;
}

export const PromptSavesChart: React.FC<PromptSavesChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return dateStr.includes('T')
      ? `${date.toLocaleDateString()} ${date.getHours()}:00`
      : date.toLocaleDateString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-3 rounded-lg shadow-md">
          <p className="font-semibold">{formatDate(label)}</p>
          <p className="text-emerald-500">{`Saves: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="col-span-4 lg:col-span-2">
      <CardHeader>
        <CardTitle>Prompt Saves</CardTitle>
      </CardHeader>
      <CardContent className="h-[250px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                minTickGap={30}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="saves" 
                name="Saves" 
                stroke="#10b981"
                fill="#10b98180" 
                activeDot={{ r: 8 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Prompt Collections Chart
interface PromptCollectionsChartProps {
  data: Array<{ date: string; collections: number }>;
  isLoading?: boolean;
}

export const PromptCollectionsChart: React.FC<PromptCollectionsChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return dateStr.includes('T')
      ? `${date.toLocaleDateString()} ${date.getHours()}:00`
      : date.toLocaleDateString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-3 rounded-lg shadow-md">
          <p className="font-semibold">{formatDate(label)}</p>
          <p className="text-amber-500">{`Collections: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="col-span-4 lg:col-span-2">
      <CardHeader>
        <CardTitle>Collection Adds</CardTitle>
      </CardHeader>
      <CardContent className="h-[250px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                minTickGap={30}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="collections" 
                name="Collections" 
                stroke="#f59e0b"
                fill="#f59e0b80" 
                activeDot={{ r: 8 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Search Volume Line Chart
interface SearchVolumeChartProps {
  data: Array<{ date: string; searches: number }>;
  isLoading?: boolean;
}

export const SearchVolumeChart: React.FC<SearchVolumeChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null; // Don't render the chart if there's no data
  }
  
  return (
    <Card className="col-span-4 lg:col-span-2">
      <CardHeader>
        <CardTitle>Search Volume</CardTitle>
      </CardHeader>
      <CardContent className="h-[250px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <ChartContainer 
            config={{ searches: { color: "#8b5cf6" } }}
            className="h-full w-full"
          >
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
                hide 
              />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line 
                type="monotone" 
                dataKey="searches" 
                name="Searches" 
                stroke="#8b5cf6"
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Rating Distribution Pie Chart
interface RatingDistributionChartProps {
  data: Array<{ rating: number; count: number }>;
  isLoading?: boolean;
}

export const RatingDistributionChart: React.FC<RatingDistributionChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null; // Don't render the chart if there's no data
  }

  const COLORS = ['#ef4444', '#f59e0b', '#a3e635', '#06b6d4', '#2563eb'];
  
  return (
    <Card className="col-span-4 lg:col-span-2">
      <CardHeader>
        <CardTitle>Rating Distribution</CardTitle>
      </CardHeader>
      <CardContent className="h-[250px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <ChartContainer
            config={{
              rating1: { color: COLORS[0] },
              rating2: { color: COLORS[1] },
              rating3: { color: COLORS[2] },
              rating4: { color: COLORS[3] },
              rating5: { color: COLORS[4] },
            }}
            className="h-full w-full"
          >
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ rating, count, percent }) => `${rating}★: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
                nameKey="rating"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[entry.rating - 1]} />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Premium Conversions Line Chart
interface PremiumConversionsChartProps {
  data: Array<{ date: string; conversions: number }>;
  isLoading?: boolean;
}

export const PremiumConversionsChart: React.FC<PremiumConversionsChartProps> = ({ 
  data, 
  isLoading = false 
}) => {
  if (!data || data.length === 0) {
    return null; // Don't render the chart if there's no data
  }

  return (
    <Card className="col-span-4">
      <CardHeader>
        <CardTitle>Premium Conversions</CardTitle>
      </CardHeader>
      <CardContent className="h-[250px]">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <ChartContainer 
            config={{ conversions: { color: "#ec4899" } }}
            className="h-full w-full"
          >
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line 
                type="monotone" 
                dataKey="conversions" 
                name="Conversions" 
                stroke="#ec4899"
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
